if (typeof debounce === 'undefined') {
  var debounce = function(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  };
}
/**
 *  @class
 *  @function PredictiveSearch
 */
class PredictiveSearch {
  constructor() {
    this.container = document.getElementById('Search-Drawer');
    if (!this.container) return;
    this.form = this.container.querySelector('form.searchform');
    this.input = this.container.querySelector('input[type="search"]');
    // Absent when Theme settings > Search > Predictive search is off: the panel then only opens and submits.
    this.predictiveSearchResults = this.container.querySelector('.side-panel-content--has-tabs');
    this.initialContent = this.container.querySelector('.side-panel-content--initial');
    this.status = this.container.querySelector('[data-predictive-search-status]');
    this.cache = new Map();
    this.controller = null;
    this.promotionRequested = false;

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.form.addEventListener('submit', this.onFormSubmit.bind(this));

    // Delegated, so every search trigger opens the panel, including ones the theme editor re-renders.
    document.addEventListener('click', (event) => {
      if (!event.target.closest('.thb-quick-search')) return;
      event.preventDefault();
      if (OverlayManager.isOpen(this.container)) {
        OverlayManager.close(this.container);
      } else {
        this.loadPromotion();
        OverlayManager.open(this.container);
        setTimeout(() => {
          this.input.focus({
            preventScroll: true
          });
        }, 100);
        dispatchCustomEvent('search:open');
      }
    });

    // ArrowDown/ArrowUp move between the field and the links shown under it (results, or recent/popular/trending).
    this.container.addEventListener('keydown', this.onKeydown.bind(this));

    if (!this.predictiveSearchResults) return;

    this.input.addEventListener('input', debounce(() => {
      this.onChange();
    }, 300));

    this.predictiveSearchResults.addEventListener('click', (event) => {
      const submitButton = event.target.closest('#search-results-submit');
      if (submitButton) {
        this.form.submit();
      }
    });
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.abortRequest();
      this.setLoading(false);
      this.setExpanded(false);
      return;
    }
    this.setExpanded(true);
    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length) {
      event.preventDefault();
    }
  }

  setExpanded(expanded) {
    this.predictiveSearchResults.classList.toggle('active', expanded);
    this.input.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }

  // The panel shows its spinner (and its click-blocking .loading layer) only while a request is in flight.
  setLoading(loading) {
    this.predictiveSearchResults.classList.toggle('loading', loading);
  }

  abortRequest() {
    if (this.controller) {
      this.controller.abort();
      this.controller = null;
    }
  }

  getSearchResults(searchTerm) {
    this.abortRequest();

    // Serve from cache if available
    if (this.cache.has(searchTerm)) {
      this.setLoading(false);
      this.renderSearchResults(this.cache.get(searchTerm));
      return;
    }

    const controller = new AbortController();
    this.controller = controller;
    this.setLoading(true);

    const params = new URLSearchParams({
      q: searchTerm,
      'resources[type]': 'product,article,query',
      'resources[limit]': '10',
      'resources[options][fields]': 'title,product_type,vendor,variants.title,variants.sku',
      section_id: 'predictive-search'
    });

    fetch(`${theme.routes.predictive_search_url}?${params.toString()}`, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.status);
        }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;

        // Cap cache size
        if (this.cache.size >= 50) {
          const firstKey = this.cache.keys().next().value;
          this.cache.delete(firstKey);
        }
        this.cache.set(searchTerm, resultsMarkup);

        // A newer query took over while this one was loading: keep its answer cached, but do not show it.
        if (controller !== this.controller) return;
        this.controller = null;
        this.setLoading(false);
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error.name === 'AbortError' || controller !== this.controller) {
          return;
        }
        this.controller = null;
        this.setLoading(false);
        this.setExpanded(false);
      });
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.announce(this.predictiveSearchResults.querySelector('[data-predictive-search-summary]')?.textContent.trim());
  }

  announce(message) {
    if (!this.status || !message) return;
    // Cleared first so the same message (e.g. the same count twice) is announced again.
    this.status.textContent = '';
    setTimeout(() => {
      this.status.textContent = message;
    }, 100);
  }

  getOptions() {
    const showingResults = this.predictiveSearchResults?.classList.contains('active');
    const scope = showingResults ? this.predictiveSearchResults : this.initialContent;
    if (!scope) return [];
    return Array.from(scope.querySelectorAll('a[href], #search-results-submit')).filter((el) => el.getClientRects().length > 0);
  }

  onKeydown(event) {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const options = this.getOptions();
    if (!options.length) return;
    const index = options.indexOf(document.activeElement);

    if (event.target === this.input) {
      if (event.key !== 'ArrowDown') return;
      event.preventDefault();
      options[0].focus();
      return;
    }
    if (index === -1) return;
    event.preventDefault();
    if (event.key === 'ArrowDown') {
      options[Math.min(index + 1, options.length - 1)].focus();
    } else if (index === 0) {
      this.input.focus();
    } else {
      options[index - 1].focus();
    }
  }

  // Trending products (sections/search-promotion.liquid) render only when the shopper first opens search.
  loadPromotion() {
    if (this.promotionRequested) return;
    const target = this.container.querySelector('.thb-predictive-search--promotion-target');
    if (!target) return;
    this.promotionRequested = true;

    fetch(`${theme.routes.root_url.replace(/\/$/, '')}/?section_id=${target.dataset.section}`)
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((text) => {
        const products = new DOMParser().parseFromString(text, 'text/html').querySelector('.thb-predictive-search--promotion-products');
        if (products) target.replaceChildren(products);
      })
      .catch(() => {
        // Try again on the next open.
        this.promotionRequested = false;
      });
  }
}
// Deferred, after app.js (OverlayManager): ready from the first tap, without waiting for the page's load event.
new PredictiveSearch();
